/**
 * AvatarCluster — shared aspect. Overlapping circular avatars with
 * an optional "+N more" overflow chip and an optional right-aligned
 * member-count meta line ("3 of 4").
 *
 * Avatars are rendered via the optional renderAvatar callback so the
 * consumer keeps its own Avatar component (initials, photo, etc.).
 * Default is an initials-in-sand circle so /brand specimens render
 * without needing the app's Avatar.
 */
import { Text, View } from "react-native";

import { COLORS, RADII, STROKES } from "../../lib/brand";
import type { PaneTheme } from "../brand/paneThemes";

export type AvatarClusterMember = {
  id: string;
  name: string;
};

export type AvatarClusterProps = {
  theme: PaneTheme;
  members: AvatarClusterMember[];
  /** Cap on visible avatars. Default 4. Anything past becomes an
   *  overflow chip ("+N"). */
  maxVisible?: number;
  /** Optional right-aligned meta like "3 of 4". */
  countMeta?: string;
  renderAvatar?: (member: AvatarClusterMember) => React.ReactNode;
};

export function AvatarCluster({
  theme,
  members,
  maxVisible = 4,
  countMeta,
  renderAvatar,
}: AvatarClusterProps) {
  const visible = members.slice(0, maxVisible);
  const overflow = Math.max(0, members.length - visible.length);
  return (
    <View style={{ flexDirection: "row", alignItems: "center" }}>
      {visible.map((m, i) => (
        <View
          key={m.id}
          style={{
            marginLeft: i === 0 ? 0 : -6,
            borderWidth: STROKES.strong,
            borderColor: theme.surfaceRaisedBg,
            borderRadius: RADII.full,
            overflow: "hidden",
          }}
        >
          {renderAvatar ? renderAvatar(m) : <DefaultAvatar name={m.name} theme={theme} />}
        </View>
      ))}
      {overflow > 0 ? (
        <View
          style={{
            marginLeft: -6,
            width: 26,
            height: 26,
            borderRadius: RADII.full,
            borderWidth: STROKES.strong,
            borderColor: theme.surfaceRaisedBg,
            backgroundColor: COLORS.SAND.hex,
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Text
            style={{
              fontFamily: theme.bodyBoldFont,
              fontSize: 9,
              color: theme.textOnSurfaceDim,
            }}
          >
            {"+" + overflow}
          </Text>
        </View>
      ) : null}
      {countMeta ? (
        <>
          <View style={{ flex: 1 }} />
          <Text
            style={{
              alignSelf: "center",
              fontFamily: theme.bodyFont,
              fontSize: 11,
              color: theme.textOnSurfaceDim,
            }}
          >
            {countMeta}
          </Text>
        </>
      ) : null}
    </View>
  );
}

function DefaultAvatar({ name, theme }: { name: string; theme: PaneTheme }) {
  const initials = name
    .split(/\s+/)
    .map((w) => (w[0] ?? "").toUpperCase())
    .join("")
    .slice(0, 2);
  return (
    <View
      style={{
        width: 26,
        height: 26,
        borderRadius: RADII.full,
        backgroundColor: COLORS.SAND.hex,
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Text
        style={{
          fontFamily: theme.bodyBoldFont,
          fontSize: 9,
          color: theme.textOnSurface,
        }}
      >
        {initials}
      </Text>
    </View>
  );
}
